import React, { useState, useEffect, useCallback, FC, useRef } from 'react';
import styled from '@emotion/styled';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useTableUniverse, useTableApi } from '~Components/TableUniverse';
import Navigation from './Components/Navigation';
import Grid from '../Tables/Scenes/Grid';
import Floorplan from './Scenes/Floorplan';
import Footer from './Components/Footer';
import TableInfo from './Components/TableInfo';
import Cheques from './Cheques';
import { VenueData, VenueDataVariables } from './__queries__';
import BongWrapper from './Components/BongWrapper';
import { useSelector, useI18n, useLogger } from '~Hooks';
import { useStorage } from '~Components/Storage';
import SelectOptionModal from '~Components/SelectOptionModal';
import { range } from 'lodash';
import BongMaster from '~Scenes/Tables/Components/BongMaster';

import { VenueData_venue_floorPlans_tables } from './__queries__';

import LoginModal from './Components/LoginModal';
import ConnectionPopover from './Components/ConnectionPopover';
import Modules from '~Components/Modules';
import { CircleSpinner } from 'react-spinners-kit';
import DeviceStatusbar from '~ComponentsImproved/DeviceStatusBar';
import CartProvider from '~Components/CartProvider';
import { useHistory, useLocation } from 'react-router';
import Onboarding from '~Scenes/Onboarding';
import Bookings from '../Bookings';

const Wrapper = styled.div`
  background-color: #1c232c;
  display: flex;
  flex-flow: row nowrap;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
`;

const LeftWrapper = styled.div`
  background-color: #1c232c;
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
  padding-top: 24px;
`;

const RightWrapper = styled.div`
  background-color: #2f353d;
  width: 400px;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex: 1;
  height: 100%;
  overflow: auto;
`;

export const Content = styled.div`
  padding: 0;
  height: 100%;
  overflow: auto;
  box-sizing: border-box;
  width: 100%;
  flex: 1;
  position: relative;
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background: rgba(14, 18, 22, 0.755);
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Loading = styled.div`
  border-radius: 4px;
  box-shadow: 0px 0px 16px 0px rgb(0 0 0 / 24%), 0px 1px 3px 0px rgb(0 0 0 / 50%);
  height: 56px;
  padding: 12px 16px;
  background-color: var(--colorUISand);
  margin-bottom: 8px;
  box-sizing: border-box;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const LoadingText = styled.div`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 16px;
  color: #1c232c;
  margin: 0 16px 0 0;
`;

const DATA_QUERY = gql`
  query VenueData($id: ID!) {
    venue: Venue(id: $id) {
      id
      name

      floorPlans {
        id
        name
        order

        markers {
          id
          posX
          posY
          size
          icon
        }

        structures {
          id
          posX
          posY
          width
          height
          rotation
        }

        tables {
          id
          code
          rotation
          posX
          posY
          deleted

          floorPlan {
            id
          }
        }
      }

      predefinedMessages {
        id
        message
        type
      }
    }
  }
`;

interface SelectedTable {
  id: string;
  name: string;
  coverage?: number;
}

const Tables: FC = () => {
  const demoMode = useSelector((state) => state.demoMode);
  let [, , venueId] = location.hash.split('/');

  if (demoMode) {
    venueId = window.DEMO_MODE_VENUE_ID;
  }

  const [showLogin, setShowLogin] = useState(false);
  const staff = useSelector((state) => state.staff);
  const initialTableDataLoaded = !!useSelector((state) => state.lastTableEventData);
  const POSMenuLoaded = Boolean(useSelector((state) => state.POSMenu));
  const universe = useTableUniverse();
  const allCheques = universe.cheques;
  const [allTables, setAllTables] = useState<VenueData_venue_floorPlans_tables[]>();
  const [selectedTable, setSelectedTable] = useState<SelectedTable | null>(null);
  const [selectedNav, setSelectedNav] = useState<string>('Grid');
  const [selectedCheque, setSelectedCheque] = useState<string | null>(null);
  const [isBongVisible, setBongVisible] = useState(false);
  const { i18n } = useI18n();
  const { search } = useLocation();
  const history = useHistory();
  const hasAddedItems = useRef(false);
  const api = useTableApi();
  const [storage] = useStorage();
  const logger = useLogger('tables');
  const staffId = useSelector((state) =>
    state.companyStaff?.staffs.length ? state.staff : false,
  );

  // State for coverage modal when double-clicking a table with no cheques
  const [showCoverageModal, setShowCoverageModal] = useState(false);
  const pendingDoubleClickTable = useRef<SelectedTable | null>(null);

  function onItemsAddedToCheque() {
    hasAddedItems.current = true;
  }

  /**
   * Make sure to un-select the cheque if it disappears for whichever reason (paid, removed etc)
   */
  const selectedChequeItem = allCheques.find((c) => c.id === selectedCheque);
  useEffect(() => {
    if (selectedCheque && !selectedChequeItem) {
      setSelectedCheque(null);
      setBongVisible(false);
    }
  }, [allCheques]);

  useEffect(() => {
    if (selectedTable) {
      return;
    }

    const searchParams = new URLSearchParams(search);
    const tableToSelect = searchParams.get('table') ?? null;

    if (!tableToSelect) {
      return;
    }

    const foundTable = allTables?.find((table) => table.id === tableToSelect);

    if (foundTable && !selectedTable) {
      setSelectedTable({ id: foundTable.id, name: foundTable.code, coverage: 0 });

      searchParams.delete('table');
    }

    // Set updated search params
    history.push({
      search: searchParams.toString(),
    });
  }, [allTables, selectedTable]);

  const { data } = useQuery<VenueData, VenueDataVariables>(DATA_QUERY, {
    variables: {
      id: venueId,
    },
    skip: !venueId,
  });

  const markers = data?.venue?.floorPlans
    .find((fp) => fp.id === selectedNav)
    ?.markers.map((marker) => marker);

  const structures = data?.venue?.floorPlans
    .find((fp) => fp.id === selectedNav)
    ?.structures.map((structure) => structure);

  useEffect(() => {
    if (data?.venue?.floorPlans && data?.venue?.floorPlans.length > 0) {
      let tables: any[] = [];
      data?.venue?.floorPlans.map((item) => {
        tables.push(...item.tables);
      });
      tables = tables.filter((item: VenueData_venue_floorPlans_tables) => !item.deleted);
      setAllTables(tables);
    }
  }, [data]);

  const handleChangeNav = (nav: string) => {
    setSelectedNav(nav);
  };

  const unSelectAll = () => {
    setSelectedTable(null);
    setSelectedCheque(null);
  };

  const onTableSelected = useCallback(
    (id: string, name: string, coverage?: number) => {
      setSelectedTable({ id, name, coverage });
    },
    [setSelectedTable],
  );

  const showBong = useCallback(() => setBongVisible(true), [setBongVisible]);
  const hideBong = useCallback(() => setBongVisible(false), [setBongVisible]);

  // Create a cheque for a table and open the ordering view
  const createChequeAndOpenBong = useCallback(
    (tableId: string, coverage: number) => {
      const chequeId = api.createCheque(tableId, coverage);
      api.log.createCheque(chequeId, tableId, coverage);
      logger.info('Created cheque via double-click', { tableId, chequeId, coverage });
      setSelectedCheque(chequeId);
      setBongVisible(true);
    },
    [api, logger],
  );

  // Handle double-click on a table: open ordering view for first cheque, or create one
  const onTableDoubleClick = useCallback(
    (id: string, name: string, coverage?: number) => {
      // Require staff login
      if (staffId === null) {
        setShowLogin(true);
        return;
      }

      // Select the table
      setSelectedTable({ id, name, coverage });

      // Find existing cheques for this table
      const tableCheques = allCheques.filter((c) => c.tableId === id);

      if (tableCheques.length === 1) {
        // Only one cheque - safe to go straight to ordering
        const cheque = tableCheques[0];
        logger.info('Opening ordering view via double-click (single cheque)', { tableId: id, chequeId: cheque.id });
        setSelectedCheque(cheque.id);
        setBongVisible(true);
      } else if (tableCheques.length > 1) {
        // Multiple cheques - let the user pick which one to order on
        logger.info('Multiple cheques on table, showing cheque panel instead of ordering', { tableId: id, chequeCount: tableCheques.length });
        setSelectedCheque(tableCheques[0].id);
      } else {
        // No cheques exist - need to create one
        if (storage.hideCoverageModal) {
          // Coverage modal disabled, create cheque with 0 coverage
          createChequeAndOpenBong(id, 0);
        } else {
          // Show coverage modal, then create cheque
          pendingDoubleClickTable.current = { id, name, coverage };
          setShowCoverageModal(true);
        }
      }
    },
    [staffId, allCheques, storage.hideCoverageModal, createChequeAndOpenBong, logger],
  );

  // Handle coverage selection from the double-click coverage modal
  const handleDoubleClickCoverage = useCallback(
    (count: number) => {
      const table = pendingDoubleClickTable.current;
      if (table) {
        createChequeAndOpenBong(table.id, count);
      }
      setShowCoverageModal(false);
      pendingDoubleClickTable.current = null;
    },
    [createChequeAndOpenBong],
  );

  const selectedChequeHasPayment = selectedChequeItem && selectedChequeItem.paidAmount > 0;

  return (
    <Wrapper>
      <ConnectionPopover />
      <DeviceStatusbar />

      <LeftWrapper>
        <InnerWrapper>
          <Navigation
            selectedNav={selectedNav}
            floorPlans={data?.venue?.floorPlans ?? []}
            changeNav={(nav: string) => handleChangeNav(nav)}
          />
          <Content onTouchStart={unSelectAll}>
            {((!demoMode && !initialTableDataLoaded) || !POSMenuLoaded) && (
              <LoadingContainer>
                <Loading>
                  <LoadingText>
                    {initialTableDataLoaded
                      ? i18n('General.LoadingMenuItems')
                      : i18n('General.LoadingTableData')}
                  </LoadingText>
                  <CircleSpinner color="#1c232c" size={16} />
                </Loading>
              </LoadingContainer>
            )}
            {selectedNav === 'Bookings' ? (
              <Bookings
                tables={data?.venue.floorPlans.flatMap((fp) => fp.tables) ?? []}
                onNavigateToTable={(tableId: string) => {
                  const foundTable = allTables?.find((table) => table.id === tableId);
                  if (foundTable) {
                    setSelectedTable({ id: foundTable.id, name: foundTable.code, coverage: 0 });
                    handleChangeNav('Grid');
                  }
                }}
              />
            ) : selectedNav == 'Grid' || selectedNav == 'Open' ? (
              <Grid
                key={selectedNav}
                tables={allTables ?? []}
                allCheques={allCheques}
                selectedTable={selectedTable?.id}
                showOnlyOpen={selectedNav === 'Open'}
                setSelectedTable={onTableSelected}
                onTableDoubleClick={onTableDoubleClick}
              />
            ) : (
              selectedNav.length > 0 && (
                <Floorplan
                  allTables={allTables ?? []}
                  markers={markers ?? []}
                  structures={structures ?? []}
                  selectedFloorPlan={selectedNav}
                  allCheques={allCheques}
                  selectedTable={selectedTable?.id}
                  setSelectedTable={onTableSelected}
                  onTableDoubleClick={onTableDoubleClick}
                />
              )
            )}
          </Content>
        </InnerWrapper>
        <Footer
          selectedTable={selectedNav === 'Bookings' ? null : selectedTable}
          selectedCheque={selectedNav === 'Bookings' ? null : selectedCheque}
          showBong={showBong}
          predefinedMessages={data?.venue?.predefinedMessages ?? []}
          showLogin={() => setShowLogin(true)}
          disableOrdering={selectedChequeHasPayment}
        />
      </LeftWrapper>
      <RightWrapper>
        {selectedNav !== 'Bookings' && selectedTable && (
          <>
            <TableInfo
              selectedTable={selectedTable}
              allCheques={allCheques}
              selectedCheque={selectedCheque}
              showLogin={() => setShowLogin(true)}
            />
            <Cheques
              onSelectCheque={setSelectedCheque}
              selectedCheque={selectedCheque}
              table={selectedTable}
              allCheques={allCheques}
              showBong={showBong}
              showLogin={() => setShowLogin(true)}
              isBongVisible={isBongVisible}
              onDelete={onItemsAddedToCheque}
            />
          </>
        )}
        {selectedNav === 'LeftoverItems' && (
          <Cheques
            onSelectCheque={() => {}}
            selectedCheque=""
            table={null}
            allCheques={allCheques}
            homelessItems
            showBong={showBong}
            showLogin={() => setShowLogin(true)}
          />
        )}
      </RightWrapper>
      {isBongVisible && selectedCheque && (
        <CartProvider isTSR>
          <BongWrapper
            venueId={venueId}
            table={selectedTable}
            chequeId={selectedCheque}
            chequeName={selectedTable?.name ?? ''}
            onCancel={hideBong}
            onSubmit={onItemsAddedToCheque}
          />
        </CartProvider>
      )}

      {staff === null && <LoginModal onClose={() => setShowLogin(false)} />}

      <BongMaster />
      <Onboarding />

      <Modules />

      {showCoverageModal && (
        <SelectOptionModal
          enableCustomInput
          title={i18n('TM.Coverage')}
          options={range(1, 8)}
          onSelect={(count) => handleDoubleClickCoverage(count as number)}
          onCancel={() => {
            setShowCoverageModal(false);
            pendingDoubleClickTable.current = null;
          }}
        />
      )}
    </Wrapper>
  );
};

export default Tables;
